import type { ChangeEvent, MouseEventHandler } from "react";
import { useState } from "react";
import cx from "classnames";

import {
  Box,
  Button,
  Checkbox,
  Typography,
} from "@braintrust/braintrust-ui-components";
import { useAccountType } from "@js/apps/common/hooks";
import { useCreateStringDependsOnSelectedJobFilters } from "@js/apps/jobs/apps/listing/hooks/use-create-string-depends-on-selected-filters";
import type { Skill } from "@js/types/admin";
import type { SavedJobFilter } from "@js/types/jobs";

import styles from "./style.module.scss";

type SavedJobSearchItemProps = {
  savedFilter: SavedJobFilter;
  onApply: () => void;

  onEmailNotificationsToggle: (
    event: ChangeEvent<HTMLInputElement>,
    filterId: number,
    enableNotifications: boolean,
  ) => void;

  isSelected: boolean;
  onRemoveClick: MouseEventHandler;
  processingSavedFilters: boolean;
  filterSkillsData: Skill[];
};

export const SavedJobSearchItem = ({
  savedFilter,
  onApply,
  onEmailNotificationsToggle,
  isSelected,
  onRemoveClick,
  processingSavedFilters,
  filterSkillsData,
}: SavedJobSearchItemProps) => {
  const [isHovering, setIsHovering] = useState(false);

  const { new_job_notification_enabled: jobNotificationEnabled, id: filterId } =
    savedFilter;

  const [checked, setChecked] = useState(jobNotificationEnabled);

  const filtersString = useCreateStringDependsOnSelectedJobFilters(
    savedFilter.filters,
    filterSkillsData,
  );

  const disabled = processingSavedFilters;

  const { isFreelancer } = useAccountType();
  const shouldShowEmailCheckbox = isFreelancer;

  return (
    <Box
      onMouseOver={() => setIsHovering(true)}
      onMouseOut={() => setIsHovering(false)}
      onClick={onApply}
      className={cx(styles.savedSearchItem, {
        [styles.savedSearchItemHovering]: isHovering && !isSelected,
        [styles.savedSearchItemSelected]: isSelected,
        [styles.savedSearchItemSquared]: isSelected && !disabled,
        [styles.savedSearchItemDisabled]: disabled,
      })}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
      }}
    >
      <Box sx={{ width: "100%", display: "flex", gap: "1.5rem" }}>
        <Box className={styles.savedSearchItemLabels} component="span">
          <Typography component="span" fontWeight={500}>
            {savedFilter.name}
          </Typography>
          <Typography component="span" color="tertiary">
            {filtersString}
          </Typography>
        </Box>

        <Button
          onMouseOver={(e) => {
            e.stopPropagation();
            setIsHovering(false);
          }}
          className={styles.savedSearchItemRemoveButton}
          disabled={disabled}
          variant="secondary"
          size="medium"
          onClick={onRemoveClick}
        >
          Remove
        </Button>
      </Box>
      {shouldShowEmailCheckbox && (
        <Box onClick={(event) => event.stopPropagation()}>
          <Checkbox
            checked={checked}
            onClick={() => setChecked(!checked)}
            onChange={(event) =>
              onEmailNotificationsToggle(event, filterId, event.target.checked)
            }
            children="Emailing new matching jobs"
          />
        </Box>
      )}
    </Box>
  );
};
